import React, { useState, useEffect, useCallback } from 'react';
import { Dialog, DialogContent, CircularProgress, Tooltip } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/DeleteOutline';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import ImageIcon from '@mui/icons-material/Image';
import BannerIcon from '@mui/icons-material/Campaign';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import ScheduleIcon from '@mui/icons-material/Schedule';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import BarChartIcon from '@mui/icons-material/BarChart';
import { useSnackbar } from 'notistack';
import moment from 'moment-jalaali';
import * as bannerService from '../services/banner.service';
import imageUploadService from '../services/imageUpload.service';
import JalaliDatePicker, { toPersianDigits } from '../components/common/JalaliDatePicker';
import { ConfirmDialog } from '../components/common';

import './EntityManagement.css';

const statusLabels: Record<string, string> = {
  active: 'فعال',
  approved: 'تایید شده',
  pending_approval: 'در انتظار تایید',
  pending_payment: 'در انتظار پرداخت',
  expired: 'منقضی',
  rejected: 'رد شده',
  cancelled: 'لغو شده',
  draft: 'پیش‌نویس',
  payment_failed: 'پرداخت ناموفق',
};

const statusTagClass: Record<string, string> = {
  active: 'tag-active',
  approved: 'tag-active',
  pending_approval: 'tag-warning',
  pending_payment: 'tag-warning',
  expired: 'tag-inactive',
  rejected: 'tag-inactive',
  cancelled: 'tag-inactive',
  draft: 'tag-platform',
  payment_failed: 'tag-inactive',
};

const linkTypeLabels: Record<string, string> = {
  product: 'محصول',
  store: 'فروشگاه',
  category: 'دسته‌بندی',
  external: 'لینک خارجی',
  custom: 'سفارشی',
};

const AdminBannerManagement: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();

  // State
  const [banners, setBanners] = useState<any[]>([]);
  const [slots, setSlots] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);

  // Filters
  const [searchInput, setSearchInput] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [slotFilter, setSlotFilter] = useState('');

  // Create/Edit Dialog
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingBanner, setEditingBanner] = useState<any | null>(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [uploadingImage, setUploadingImage] = useState<'desktop' | 'mobile' | null>(null);
  const [formError, setFormError] = useState('');

  // Details Dialog
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [detailsBanner, setDetailsBanner] = useState<any | null>(null);

  // Delete Dialog
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [toDelete, setToDelete] = useState<any | null>(null);

  const [formData, setFormData] = useState({
    slotId: '',
    startDate: '',
    endDate: '',
    content: {
      images: {
        desktop: '',
        mobile: '',
      },
      title: '',
      subtitle: '',
      description: '',
      callToAction: '',
      link: {
        type: 'external' as 'product' | 'store' | 'category' | 'external' | 'custom',
        target: '',
        openInNewTab: false,
      },
    },
  });

  // Fetch banners
  const fetchBanners = useCallback(async () => {
    try {
      setLoading(true);
      const response = await bannerService.getAdminBanners({
        page: page,
        limit: pageSize,
      });
      setBanners(response.docs || []);
      setTotalPages(response.totalPages || 1);
    } catch (err: any) {
      enqueueSnackbar('خطا در بارگذاری بنرها', { variant: 'error' });
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, enqueueSnackbar]);

  const fetchSlots = async () => {
    try {
      const response = await bannerService.getAllBannerSlots({ isActive: true });
      setSlots(response.docs || []);
    } catch (err) {
      console.error('Error fetching slots:', err);
    }
  };

  useEffect(() => {
    fetchBanners();
  }, [fetchBanners]);

  useEffect(() => {
    fetchSlots();
  }, []);

  // Handlers
  const handleSearch = () => {
    setSearchTerm(searchInput);
    setPage(1);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleSearch();
  };

  const clearFilters = () => {
    setSearchTerm('');
    setSearchInput('');
    setStatusFilter('');
    setSlotFilter('');
    setPage(1);
  };

  const openCreate = () => {
    setEditingBanner(null);
    setFormData({
      slotId: '',
      startDate: '',
      endDate: '',
      content: {
        images: { desktop: '', mobile: '' },
        title: '',
        subtitle: '',
        description: '',
        callToAction: '',
        link: { type: 'external', target: '', openInNewTab: false },
      },
    });
    setFormError('');
    setDialogOpen(true);
  };

  const openEdit = (banner: any) => {
    setEditingBanner(banner);
    setFormData({
      slotId: banner.slotId?._id || banner.slotId || '',
      startDate: banner.dates?.startDate?.split('T')[0] || '',
      endDate: banner.dates?.endDate?.split('T')[0] || '',
      content: {
        images: {
          desktop: banner.content?.images?.desktop || '',
          mobile: banner.content?.images?.mobile || '',
        },
        title: banner.content?.title || '',
        subtitle: banner.content?.subtitle || '',
        description: banner.content?.description || '',
        callToAction: banner.content?.callToAction || '',
        link: {
          type: banner.content?.link?.type || 'external',
          target: banner.content?.link?.target || '',
          openInNewTab: banner.content?.link?.openInNewTab || false,
        },
      },
    });
    setFormError('');
    setDialogOpen(true);
  };

  const closeDialog = () => {
    if (actionLoading) return;
    setDialogOpen(false);
    setEditingBanner(null);
  };

  const openDetails = (banner: any) => {
    setDetailsBanner(banner);
    setDetailsOpen(true);
  };

  const closeDetails = () => {
    setDetailsOpen(false);
    setDetailsBanner(null);
  };

  const openDelete = (banner: any) => {
    setToDelete(banner);
    setDeleteOpen(true);
  };

  const closeDelete = () => {
    if (actionLoading) return;
    setDeleteOpen(false);
    setToDelete(null);
  };

  const handleImageUpload = (field: 'desktop' | 'mobile') => async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const { validFiles, errors } = imageUploadService.validateFiles([file], { maxSize: 5 * 1024 * 1024 });
    if (errors.length > 0) {
      enqueueSnackbar(errors[0], { variant: 'error' });
      return;
    }

    try {
      setUploadingImage(field);
      const result = await imageUploadService.uploadImage(validFiles[0], 'banners');
      if (result && result.url) {
        setFormData((prev) => ({
          ...prev,
          content: {
            ...prev.content,
            images: {
              ...prev.content.images,
              [field]: result.url,
            },
          },
        }));
        enqueueSnackbar(`تصویر ${field === 'desktop' ? 'دسکتاپ' : 'موبایل'} آپلود شد`, { variant: 'success' });
      } else {
        enqueueSnackbar('خطا در آپلود تصویر', { variant: 'error' });
      }
    } catch (err: any) {
      enqueueSnackbar(err.message || 'خطا در آپلود تصویر', { variant: 'error' });
    } finally {
      setUploadingImage(null);
    }
  };

  const handleSubmit = async () => {
    setFormError('');

    if (!formData.slotId || !formData.startDate || !formData.endDate) {
      setFormError('لطفا فیلدهای اجباری را پر کنید');
      return;
    }

    if (!formData.content.images.desktop || !formData.content.images.mobile) {
      setFormError('تصاویر دسکتاپ و موبایل الزامی هستند');
      return;
    }

    try {
      setActionLoading(true);
      if (editingBanner) {
        await bannerService.updateAdminBanner(editingBanner._id, formData);
        enqueueSnackbar('بنر با موفقیت بروزرسانی شد', { variant: 'success' });
      } else {
        await bannerService.createAdminBanner(formData);
        enqueueSnackbar('بنر با موفقیت ایجاد شد', { variant: 'success' });
      }
      setDialogOpen(false);
      setEditingBanner(null);
      fetchBanners();
    } catch (err: any) {
      setFormError(err?.message || 'خطا در ذخیره بنر');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!toDelete) return;
    try {
      setActionLoading(true);
      await bannerService.deleteAdminBanner(toDelete._id);
      enqueueSnackbar('بنر با موفقیت حذف شد', { variant: 'success' });
      setDeleteOpen(false);
      setToDelete(null);
      fetchBanners();
    } catch (err: any) {
      enqueueSnackbar(err.message || 'خطا در حذف بنر', { variant: 'error' });
    } finally {
      setActionLoading(false);
    }
  };

  const handleToggleVisibility = async (banner: any) => {
    try {
      const newVisibility = !banner.display?.isVisible;
      await bannerService.toggleAdminBannerVisibility(banner._id, newVisibility);
      enqueueSnackbar(`بنر ${newVisibility ? 'فعال' : 'غیرفعال'} شد`, { variant: 'success' });
      fetchBanners();
    } catch (err: any) {
      enqueueSnackbar(err.message || 'خطا در تغییر وضعیت', { variant: 'error' });
    }
  };

  const formatJalali = (date?: string) => {
    if (!date) return '-';
    return toPersianDigits(moment(date).format('jYYYY/jMM/jDD'));
  };

  const renderStatusTag = (status?: string) => {
    if (!status) return <span className="tag tag-sm tag-inactive">نامشخص</span>;
    const cls = statusTagClass[status] || 'tag-admin';
    const label = statusLabels[status] || status;
    return <span className={`tag tag-sm ${cls}`}>{label}</span>;
  };

  // Client-side filtering (search, status, slot)
  const filteredBanners = banners.filter((b) => {
    if (searchTerm) {
      const q = searchTerm.toLowerCase();
      const title = (b.content?.title || '').toLowerCase();
      const subtitle = (b.content?.subtitle || '').toLowerCase();
      const slotName = (b.slotId?.name || '').toLowerCase();
      if (!title.includes(q) && !subtitle.includes(q) && !slotName.includes(q)) return false;
    }
    if (statusFilter && b.status !== statusFilter) return false;
    if (slotFilter) {
      const sid = b.slotId?._id || b.slotId;
      if (sid !== slotFilter) return false;
    }
    return true;
  });

  const totalCount = filteredBanners.length;
  const startItem = (page - 1) * pageSize + 1;
  const endItem = Math.min(page * pageSize, totalCount);

  // Stats
  const stats = {
    total: banners.length,
    active: banners.filter((b) => b.status === 'active' || b.status === 'approved').length,
    pending: banners.filter((b) => b.status === 'pending_approval' || b.status === 'pending_payment' || b.status === 'draft').length,
    expired: banners.filter((b) => b.status === 'expired' || b.status === 'cancelled' || b.status === 'rejected').length,
  };

  const hasActiveFilters = !!(searchTerm || statusFilter || slotFilter);

  return (
    <div className="entity-page">
      {/* Header */}
      <div className="entity-header">
        <div>
          <h1 className="entity-title">بنرهای من</h1>
          <p className="entity-subtitle">مدیریت بنرهای شخصی ادمین</p>
        </div>
        <div className="header-actions">
          <button className="header-btn refresh" onClick={fetchBanners} title="بروزرسانی">
            <RefreshIcon fontSize="small" />
          </button>
          <button className="header-btn primary" onClick={openCreate}>
            <AddIcon fontSize="small" />
            بنر جدید
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="entity-stats">
        <div className="entity-stat-card">
          <div className="entity-stat-icon blue"><BannerIcon /></div>
          <div>
            <p className="entity-stat-label">کل بنرها</p>
            <p className="entity-stat-value">{toPersianDigits(String(stats.total))}</p>
          </div>
        </div>
        <div className="entity-stat-card">
          <div className="entity-stat-icon green"><CheckCircleIcon /></div>
          <div>
            <p className="entity-stat-label">فعال</p>
            <p className="entity-stat-value">{toPersianDigits(String(stats.active))}</p>
          </div>
        </div>
        <div className="entity-stat-card">
          <div className="entity-stat-icon orange"><ScheduleIcon /></div>
          <div>
            <p className="entity-stat-label">در انتظار</p>
            <p className="entity-stat-value">{toPersianDigits(String(stats.pending))}</p>
          </div>
        </div>
        <div className="entity-stat-card">
          <div className="entity-stat-icon red"><CancelIcon /></div>
          <div>
            <p className="entity-stat-label">منقضی / لغو شده</p>
            <p className="entity-stat-value">{toPersianDigits(String(stats.expired))}</p>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="entity-filters">
        <div className="filters-grid">
          <div className="filter-group">
            <label className="filter-label">
              <SearchIcon fontSize="small" />
              جستجو
            </label>
            <div className="filter-search-wrap">
              <input
                className="filter-field"
                type="text"
                placeholder="جستجو در بنرها..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyPress={handleKeyPress}
              />
              <span className="filter-search-icon">
                <SearchIcon fontSize="small" />
              </span>
            </div>
          </div>

          <div className="filter-group">
            <label className="filter-label">وضعیت</label>
            <select
              className="filter-field"
              value={statusFilter}
              onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
            >
              <option value="">همه وضعیت‌ها</option>
              <option value="active">فعال</option>
              <option value="approved">تایید شده</option>
              <option value="pending_approval">در انتظار تایید</option>
              <option value="pending_payment">در انتظار پرداخت</option>
              <option value="draft">پیش‌نویس</option>
              <option value="expired">منقضی</option>
              <option value="rejected">رد شده</option>
              <option value="cancelled">لغو شده</option>
            </select>
          </div>

          <div className="filter-group">
            <label className="filter-label">محل بنر</label>
            <select
              className="filter-field"
              value={slotFilter}
              onChange={(e) => { setSlotFilter(e.target.value); setPage(1); }}
            >
              <option value="">همه محل‌ها</option>
              {slots.map((slot) => (
                <option key={slot._id} value={slot._id}>
                  {slot.name} ({slot.code})
                </option>
              ))}
            </select>
          </div>

          <div className="filter-group filter-group-actions">
            <button className="filter-btn" onClick={handleSearch}>
              <SearchIcon fontSize="small" />
              اعمال
            </button>
            {hasActiveFilters && (
              <button className="filter-btn outline" onClick={clearFilters}>
                پاک
              </button>
            )}
          </div>
        </div>

        {hasActiveFilters && (
          <div className="filters-active">
            <span className="filters-label">فیلترهای فعال:</span>
            {searchTerm && (
              <span className="filter-tag">
                جستجو: {searchTerm}
                <button onClick={() => { setSearchTerm(''); setSearchInput(''); }}>×</button>
              </span>
            )}
            {statusFilter && (
              <span className="filter-tag">
                وضعیت: {statusLabels[statusFilter] || statusFilter}
                <button onClick={() => setStatusFilter('')}>×</button>
              </span>
            )}
            {slotFilter && (
              <span className="filter-tag">
                محل: {slots.find((s) => s._id === slotFilter)?.name || slotFilter}
                <button onClick={() => setSlotFilter('')}>×</button>
              </span>
            )}
            <button className="filters-clear" onClick={clearFilters}>پاک کردن همه</button>
          </div>
        )}
      </div>

      {/* Table */}
      <div className="entity-table-wrapper">
        {loading && banners.length === 0 ? (
          <div className="entity-loading"><CircularProgress size={32} /></div>
        ) : filteredBanners.length === 0 ? (
          <div className="entity-empty">
            <div className="entity-empty-icon"><BannerIcon style={{ fontSize: 48 }} /></div>
            <p className="entity-empty-text">هیچ بنری یافت نشد</p>
          </div>
        ) : (
          <>
            <table className="entity-table">
              <thead>
                <tr>
                  <th>پیش‌نمایش</th>
                  <th>محل بنر</th>
                  <th>وضعیت</th>
                  <th>تاریخ شروع</th>
                  <th>تاریخ پایان</th>
                  <th>کلیک / نمایش</th>
                  <th style={{ width: 180 }}>عملیات</th>
                </tr>
              </thead>
              <tbody>
                {filteredBanners.map((banner) => {
                  const clicks = banner.analytics?.clicks || banner.stats?.clicks || 0;
                  const impressions = banner.analytics?.impressions || banner.stats?.impressions || 0;
                  return (
                    <tr key={banner._id}>
                      <td>
                        <div className="entity-info-cell">
                          <div className="entity-image">
                            {banner.content?.images?.desktop ? (
                              <img
                                src={banner.content.images.desktop}
                                alt={banner.content?.title || 'banner'}
                                onError={(e) => { e.currentTarget.style.display = 'none'; }}
                              />
                            ) : (
                              <ImageIcon />
                            )}
                          </div>
                          <div className="entity-info">
                            <span className="entity-name">{banner.content?.title || 'بدون عنوان'}</span>
                          </div>
                        </div>
                      </td>
                      <td>
                        <div style={{ fontWeight: 600 }}>{banner.slotId?.name || 'نامشخص'}</div>
                        <div style={{ fontSize: 12, color: 'var(--color-text-secondary)' }}>
                          {banner.slotId?.code || ''}
                        </div>
                      </td>
                      <td>{renderStatusTag(banner.status)}</td>
                      <td>
                        <span className="cell-code">{formatJalali(banner.dates?.startDate)}</span>
                      </td>
                      <td>
                        <span className="cell-code">{formatJalali(banner.dates?.endDate)}</span>
                      </td>
                      <td>
                        <span style={{ fontSize: 13 }}>
                          {toPersianDigits(String(clicks))} / {toPersianDigits(String(impressions))}
                        </span>
                      </td>
                      <td>
                        <div className="actions-cell">
                          <Tooltip title="مشاهده جزئیات" arrow>
                            <button className="action-btn edit" onClick={() => openDetails(banner)}>
                              <BarChartIcon />
                            </button>
                          </Tooltip>
                          <Tooltip title={banner.display?.isVisible ? 'مخفی کردن' : 'نمایش دادن'} arrow>
                            <button
                              className={`action-btn toggle ${banner.display?.isVisible ? 'active' : ''}`}
                              onClick={() => handleToggleVisibility(banner)}
                            >
                              {banner.display?.isVisible ? <VisibilityOffIcon /> : <VisibilityIcon />}
                            </button>
                          </Tooltip>
                          <Tooltip title="ویرایش" arrow>
                            <button className="action-btn" onClick={() => openEdit(banner)}>
                              <EditIcon />
                            </button>
                          </Tooltip>
                          <Tooltip title="حذف" arrow>
                            <button className="action-btn delete" onClick={() => openDelete(banner)}>
                              <DeleteIcon />
                            </button>
                          </Tooltip>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            {/* Mobile Cards */}
            <div className="entity-cards">
              {filteredBanners.map((banner) => {
                const clicks = banner.analytics?.clicks || banner.stats?.clicks || 0;
                const impressions = banner.analytics?.impressions || banner.stats?.impressions || 0;
                const isInactive = banner.status === 'expired' || banner.status === 'cancelled' || banner.status === 'rejected';
                return (
                  <div key={banner._id} className={`entity-card ${isInactive ? 'inactive' : ''}`}>
                    <div className="entity-card-header">
                      <div className="entity-card-image">
                        {banner.content?.images?.desktop ? (
                          <img
                            src={banner.content.images.desktop}
                            alt={banner.content?.title || 'banner'}
                            onError={(e) => { e.currentTarget.style.display = 'none'; }}
                          />
                        ) : (
                          <ImageIcon />
                        )}
                      </div>
                      <div className="entity-card-info">
                        <div className="entity-card-name">{banner.content?.title || 'بدون عنوان'}</div>
                        <div className="entity-card-desc">
                          {banner.slotId?.name || 'نامشخص'} • {formatJalali(banner.dates?.startDate)} تا {formatJalali(banner.dates?.endDate)}
                        </div>
                      </div>
                      <div className="entity-card-status">
                        {renderStatusTag(banner.status)}
                      </div>
                    </div>
                    <div style={{ padding: '8px 14px', display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                      <span className="tag tag-sm tag-platform">
                        کلیک: {toPersianDigits(String(clicks))}
                      </span>
                      <span className="tag tag-sm tag-admin">
                        نمایش: {toPersianDigits(String(impressions))}
                      </span>
                    </div>
                    <div className="entity-card-actions">
                      <button className="entity-card-btn" onClick={() => openDetails(banner)}>
                        <BarChartIcon fontSize="small" /> جزئیات
                      </button>
                      <button className="entity-card-btn" onClick={() => handleToggleVisibility(banner)}>
                        {banner.display?.isVisible ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                        {banner.display?.isVisible ? 'مخفی' : 'نمایش'}
                      </button>
                      <button className="entity-card-btn primary" onClick={() => openEdit(banner)}>
                        <EditIcon fontSize="small" /> ویرایش
                      </button>
                      <button className="entity-card-btn danger" onClick={() => openDelete(banner)}>
                        <DeleteIcon fontSize="small" /> حذف
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Pagination */}
            <div className="entity-pagination">
              <div className="pagination-info">
                نمایش {toPersianDigits(String(startItem))} تا {toPersianDigits(String(endItem))} از {toPersianDigits(String(totalCount))}
              </div>
              <div className="pagination-controls">
                <select
                  className="pagination-select"
                  value={pageSize}
                  onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }}
                >
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
                <button className="pagination-btn" onClick={() => setPage(page + 1)} disabled={page >= totalPages}>
                  <ChevronRightIcon fontSize="small" />
                </button>
                <span style={{ fontSize: 12 }}>{toPersianDigits(String(page))} / {toPersianDigits(String(totalPages || 1))}</span>
                <button className="pagination-btn" onClick={() => setPage(page - 1)} disabled={page <= 1}>
                  <ChevronLeftIcon fontSize="small" />
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={closeDialog} maxWidth="md" fullWidth className="adminbanner-edit-dialog">
        <div className="edit-dialog-title">
          <span>{editingBanner ? 'ویرایش بنر' : 'ایجاد بنر جدید'}</span>
          <button className="details-close" onClick={closeDialog}>
            <CloseIcon fontSize="small" />
          </button>
        </div>
        <DialogContent className="edit-dialog-content">
          {formError && <div className="dialog-error">{formError}</div>}
          <div className="edit-form">
            {/* Basic info */}
            <div className="edit-card">
              <div className="edit-card-header">اطلاعات اصلی</div>
              <div className="edit-card-body">
                <div className="edit-form-grid">
                  <div className="edit-field full">
                    <label>عنوان</label>
                    <input
                      type="text"
                      value={formData.content.title}
                      onChange={(e) => setFormData((p) => ({ ...p, content: { ...p.content, title: e.target.value } }))}
                      placeholder="عنوان بنر"
                      disabled={actionLoading}
                    />
                  </div>
                  <div className="edit-field">
                    <label>زیرعنوان</label>
                    <input
                      type="text"
                      value={formData.content.subtitle}
                      onChange={(e) => setFormData((p) => ({ ...p, content: { ...p.content, subtitle: e.target.value } }))}
                      placeholder="زیرعنوان بنر"
                      disabled={actionLoading}
                    />
                  </div>
                  <div className="edit-field">
                    <label>متن دکمه (CTA)</label>
                    <input
                      type="text"
                      value={formData.content.callToAction}
                      onChange={(e) => setFormData((p) => ({ ...p, content: { ...p.content, callToAction: e.target.value } }))}
                      placeholder="مثال: مشاهده"
                      disabled={actionLoading}
                    />
                  </div>
                  <div className="edit-field full">
                    <label>توضیحات</label>
                    <textarea
                      rows={2}
                      value={formData.content.description}
                      onChange={(e) => setFormData((p) => ({ ...p, content: { ...p.content, description: e.target.value } }))}
                      disabled={actionLoading}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Slot selection */}
            <div className="edit-card">
              <div className="edit-card-header">محل نمایش بنر</div>
              <div className="edit-card-body">
                <div className="edit-form-grid">
                  <div className="edit-field full">
                    <label>محل بنر <span style={{ color: '#dc2626' }}>*</span></label>
                    <select
                      value={formData.slotId}
                      onChange={(e) => setFormData((p) => ({ ...p, slotId: e.target.value }))}
                      disabled={actionLoading}
                    >
                      <option value="">انتخاب کنید...</option>
                      {slots.map((slot) => (
                        <option key={slot._id} value={slot._id}>
                          [{slot.code}] {slot.name} ({slot.page} - {slot.position})
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            </div>

            {/* Image upload */}
            <div className="edit-card">
              <div className="edit-card-header">تصاویر بنر</div>
              <div className="edit-card-body">
                <div style={{ fontSize: 12, color: 'var(--color-text-secondary)', marginBottom: 12 }}>
                  حداکثر سایز: {toPersianDigits('5MB')} | فرمت‌های مجاز: JPG، PNG، WEBP، GIF
                </div>
                <div className="edit-form-grid">
                  {/* Desktop */}
                  <div className="edit-field">
                    <label>تصویر دسکتاپ <span style={{ color: '#dc2626' }}>*</span></label>
                    <div
                      style={{
                        border: '2px dashed var(--color-border)',
                        borderRadius: 8,
                        padding: 16,
                        textAlign: 'center',
                        minHeight: 160,
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        alignItems: 'center',
                        background: 'var(--color-bg-secondary, #f8fafc)',
                      }}
                    >
                      {uploadingImage === 'desktop' ? (
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
                          <CircularProgress size={32} />
                          <span style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>در حال آپلود...</span>
                        </div>
                      ) : formData.content.images.desktop ? (
                        <img
                          src={formData.content.images.desktop}
                          alt="Desktop Preview"
                          style={{ maxWidth: '100%', maxHeight: 120, objectFit: 'contain', marginBottom: 8 }}
                        />
                      ) : (
                        <ImageIcon style={{ fontSize: 48, color: 'var(--color-border)', marginBottom: 8 }} />
                      )}
                      <label
                        style={{
                          cursor: uploadingImage ? 'not-allowed' : 'pointer',
                          padding: '8px 16px',
                          background: uploadingImage ? '#E9B4C3' : '#B25373',
                          color: '#fff',
                          borderRadius: 6,
                          fontSize: 14,
                          opacity: uploadingImage ? 0.7 : 1,
                        }}
                      >
                        {uploadingImage === 'desktop' ? 'در حال آپلود...' : 'آپلود تصویر'}
                        <input
                          type="file"
                          hidden
                          accept="image/*"
                          onChange={handleImageUpload('desktop')}
                          disabled={actionLoading || !!uploadingImage}
                        />
                      </label>
                    </div>
                  </div>

                  {/* Mobile */}
                  <div className="edit-field">
                    <label>تصویر موبایل <span style={{ color: '#dc2626' }}>*</span></label>
                    <div
                      style={{
                        border: '2px dashed var(--color-border)',
                        borderRadius: 8,
                        padding: 16,
                        textAlign: 'center',
                        minHeight: 160,
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        alignItems: 'center',
                        background: 'var(--color-bg-secondary, #f8fafc)',
                      }}
                    >
                      {uploadingImage === 'mobile' ? (
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
                          <CircularProgress size={32} />
                          <span style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>در حال آپلود...</span>
                        </div>
                      ) : formData.content.images.mobile ? (
                        <img
                          src={formData.content.images.mobile}
                          alt="Mobile Preview"
                          style={{ maxWidth: '100%', maxHeight: 120, objectFit: 'contain', marginBottom: 8 }}
                        />
                      ) : (
                        <ImageIcon style={{ fontSize: 48, color: 'var(--color-border)', marginBottom: 8 }} />
                      )}
                      <label
                        style={{
                          cursor: uploadingImage ? 'not-allowed' : 'pointer',
                          padding: '8px 16px',
                          background: uploadingImage ? '#E9B4C3' : '#B25373',
                          color: '#fff',
                          borderRadius: 6,
                          fontSize: 14,
                          opacity: uploadingImage ? 0.7 : 1,
                        }}
                      >
                        {uploadingImage === 'mobile' ? 'در حال آپلود...' : 'آپلود تصویر'}
                        <input
                          type="file"
                          hidden
                          accept="image/*"
                          onChange={handleImageUpload('mobile')}
                          disabled={actionLoading || !!uploadingImage}
                        />
                      </label>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Schedule */}
            <div className="edit-card">
              <div className="edit-card-header">زمانبندی نمایش</div>
              <div className="edit-card-body">
                <div className="edit-form-grid">
                  <div className="edit-field">
                    <label>تاریخ شروع <span style={{ color: '#dc2626' }}>*</span></label>
                    <JalaliDatePicker
                      value={formData.startDate || ''}
                      onChange={(iso) => setFormData((p) => ({ ...p, startDate: iso.slice(0, 10) }))}
                      outputDateOnly
                    />
                  </div>
                  <div className="edit-field">
                    <label>تاریخ پایان <span style={{ color: '#dc2626' }}>*</span></label>
                    <JalaliDatePicker
                      value={formData.endDate || ''}
                      onChange={(iso) => setFormData((p) => ({ ...p, endDate: iso.slice(0, 10) }))}
                      outputDateOnly
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Link settings */}
            <div className="edit-card">
              <div className="edit-card-header">تنظیمات لینک</div>
              <div className="edit-card-body">
                <div className="edit-form-grid">
                  <div className="edit-field">
                    <label>نوع لینک</label>
                    <select
                      value={formData.content.link.type}
                      onChange={(e) => setFormData((p) => ({
                        ...p,
                        content: { ...p.content, link: { ...p.content.link, type: e.target.value as any } },
                      }))}
                      disabled={actionLoading}
                    >
                      <option value="product">محصول</option>
                      <option value="store">فروشگاه</option>
                      <option value="category">دسته‌بندی</option>
                      <option value="external">لینک خارجی</option>
                      <option value="custom">سفارشی</option>
                    </select>
                  </div>
                  <div className="edit-field">
                    <label>آدرس لینک</label>
                    <input
                      type="text"
                      value={formData.content.link.target}
                      onChange={(e) => setFormData((p) => ({
                        ...p,
                        content: { ...p.content, link: { ...p.content.link, target: e.target.value } },
                      }))}
                      placeholder="مثال: https://example.com یا /products/xyz"
                      disabled={actionLoading}
                    />
                  </div>
                  <div className="edit-field full">
                    <label className="edit-switch-item" style={{ cursor: 'pointer' }}>
                      <div>
                        <div className="edit-switch-label">باز کردن در تب جدید</div>
                        <div className="edit-switch-desc">کلیک روی بنر در صفحه جدید باز می‌شود</div>
                      </div>
                      <input
                        type="checkbox"
                        checked={formData.content.link.openInNewTab}
                        onChange={(e) => setFormData((p) => ({
                          ...p,
                          content: { ...p.content, link: { ...p.content.link, openInNewTab: e.target.checked } },
                        }))}
                        disabled={actionLoading}
                        style={{ width: 18, height: 18 }}
                      />
                    </label>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </DialogContent>
        <div className="edit-dialog-actions">
          <button className="edit-btn cancel" onClick={closeDialog} disabled={actionLoading}>انصراف</button>
          <button className="edit-btn submit" onClick={handleSubmit} disabled={actionLoading}>
            {actionLoading && <CircularProgress size={16} style={{ color: '#fff' }} />}
            {editingBanner ? 'بروزرسانی' : 'ایجاد بنر'}
          </button>
        </div>
      </Dialog>

      {/* Details Dialog */}
      <Dialog open={detailsOpen} onClose={closeDetails} maxWidth="md" fullWidth className="adminbanner-details-dialog">
        <div className="edit-dialog-title">
          <span>جزئیات بنر</span>
          <button className="details-close" onClick={closeDetails}>
            <CloseIcon fontSize="small" />
          </button>
        </div>
        <DialogContent className="edit-dialog-content">
          {detailsBanner && (
            <div className="edit-form">
              {/* Banner preview */}
              <div className="edit-card">
                <div className="edit-card-header">پیش‌نمایش بنر</div>
                <div className="edit-card-body">
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    {detailsBanner.content?.images?.desktop && (
                      <div style={{ width: '100%', borderRadius: 8, overflow: 'hidden', border: '1px solid var(--color-border)' }}>
                        <img
                          src={detailsBanner.content.images.desktop}
                          alt="Desktop"
                          style={{ width: '100%', maxHeight: 240, objectFit: 'contain', display: 'block' }}
                        />
                      </div>
                    )}
                    <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                      <div style={{ flex: 1, minWidth: 200 }}>
                        <div style={{ fontSize: 12, color: 'var(--color-text-secondary)', marginBottom: 4 }}>عنوان</div>
                        <div style={{ fontWeight: 600 }}>{detailsBanner.content?.title || '-'}</div>
                      </div>
                      <div style={{ flex: 1, minWidth: 200 }}>
                        <div style={{ fontSize: 12, color: 'var(--color-text-secondary)', marginBottom: 4 }}>زیرعنوان</div>
                        <div>{detailsBanner.content?.subtitle || '-'}</div>
                      </div>
                    </div>
                    {detailsBanner.content?.description && (
                      <div>
                        <div style={{ fontSize: 12, color: 'var(--color-text-secondary)', marginBottom: 4 }}>توضیحات</div>
                        <div style={{ fontSize: 14 }}>{detailsBanner.content.description}</div>
                      </div>
                    )}
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                      {renderStatusTag(detailsBanner.status)}
                      <span className="tag tag-sm tag-platform">
                        {detailsBanner.slotId?.name || 'نامشخص'}
                      </span>
                      {detailsBanner.display?.isVisible !== undefined && (
                        <span className={`tag tag-sm ${detailsBanner.display.isVisible ? 'tag-active' : 'tag-inactive'}`}>
                          {detailsBanner.display.isVisible ? 'در حال نمایش' : 'مخفی'}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Performance stats */}
              <div className="edit-card">
                <div className="edit-card-header">آمار عملکرد</div>
                <div className="edit-card-body">
                  <div className="edit-form-grid">
                    <div className="edit-field">
                      <label>تعداد نمایش (Impressions)</label>
                      <div style={{ fontSize: 20, fontWeight: 700, color: '#B25373' }}>
                        {toPersianDigits(String(detailsBanner.analytics?.impressions || detailsBanner.stats?.impressions || 0))}
                      </div>
                    </div>
                    <div className="edit-field">
                      <label>تعداد کلیک</label>
                      <div style={{ fontSize: 20, fontWeight: 700, color: '#16a34a' }}>
                        {toPersianDigits(String(detailsBanner.analytics?.clicks || detailsBanner.stats?.clicks || 0))}
                      </div>
                    </div>
                    <div className="edit-field">
                      <label>نرخ کلیک (CTR)</label>
                      <div style={{ fontSize: 20, fontWeight: 700, color: '#9333ea' }}>
                        {(() => {
                          const impressions = detailsBanner.analytics?.impressions || detailsBanner.stats?.impressions || 0;
                          const clicks = detailsBanner.analytics?.clicks || detailsBanner.stats?.clicks || 0;
                          const ctr = impressions > 0 ? (clicks / impressions) * 100 : 0;
                          return `${toPersianDigits(ctr.toFixed(2))}٪`;
                        })()}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Schedule info */}
              <div className="edit-card">
                <div className="edit-card-header">اطلاعات زمانبندی و لینک</div>
                <div className="edit-card-body">
                  <div className="edit-form-grid">
                    <div className="edit-field">
                      <label>تاریخ شروع</label>
                      <div className="cell-code">{formatJalali(detailsBanner.dates?.startDate)}</div>
                    </div>
                    <div className="edit-field">
                      <label>تاریخ پایان</label>
                      <div className="cell-code">{formatJalali(detailsBanner.dates?.endDate)}</div>
                    </div>
                    <div className="edit-field">
                      <label>نوع لینک</label>
                      <div>{linkTypeLabels[detailsBanner.content?.link?.type] || '-'}</div>
                    </div>
                    <div className="edit-field">
                      <label>آدرس لینک</label>
                      <div style={{ wordBreak: 'break-all', fontSize: 13 }}>
                        {detailsBanner.content?.link?.target || '-'}
                      </div>
                    </div>
                    <div className="edit-field">
                      <label>تب جدید</label>
                      <div>{detailsBanner.content?.link?.openInNewTab ? 'بله' : 'خیر'}</div>
                    </div>
                    <div className="edit-field">
                      <label>متن دکمه</label>
                      <div>{detailsBanner.content?.callToAction || '-'}</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
        <div className="edit-dialog-actions">
          <button className="edit-btn cancel" onClick={closeDetails}>بستن</button>
          {detailsBanner && (
            <button
              className="edit-btn submit"
              onClick={() => {
                closeDetails();
                openEdit(detailsBanner);
              }}
            >
              ویرایش بنر
            </button>
          )}
        </div>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        open={deleteOpen}
        title="حذف بنر"
        message={
          <span>
            آیا از حذف <strong>«{toDelete?.content?.title || 'این بنر'}»</strong> اطمینان دارید؟
            <br />این عملیات غیرقابل بازگشت است.
          </span>
        }
        cancelText="انصراف"
        confirmText="حذف بنر"
        confirmColor="error"
        loading={actionLoading}
        onCancel={closeDelete}
        onConfirm={handleDelete}
      />
    </div>
  );
};

export default AdminBannerManagement;
